import React, { useState } from 'react';
import { Bell, Search, LogOut } from 'lucide-react';
import { useCompany } from '../lib/hooks';
import { supabase } from '../lib/supabase';
import GlobalSearch from './GlobalSearch';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface HeaderProps {
  collapsed?: boolean;
}

const Header: React.FC<HeaderProps> = ({ collapsed = false }) => {
  const { company, loading } = useCompany();
  const [showSearch, setShowSearch] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  return (
    <header className={cn(
      "bg-background/90 backdrop-blur-md border-b border-border sticky top-0 z-50",
      "transition-all duration-300",
      collapsed ? "lg:pl-16" : "lg:pl-64"
    )}>
      <div className="h-16 px-4">
        <div className="flex h-full items-center justify-end gap-4">
          {/* Right Section - Search, Notifications & Profile */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="text-muted-foreground"
              onClick={() => setShowSearch(true)}
            >
              <Search className="w-4 h-4 mr-2" />
              Search
              <kbd className="ml-2 pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                <span className="text-xs">⌘</span>K
              </kbd>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="relative"
            >
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse-slow" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {company?.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {loading ? (
                  <div className="p-2">
                    <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col p-2">
                      <p className="text-sm font-medium">{company?.name}</p>
                      <p className="text-xs text-muted-foreground">{company?.email}</p>
                    </div>
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-destructive focus:text-destructive"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Global Search */}
      <GlobalSearch open={showSearch} onOpenChange={setShowSearch} />
    </header>
  );
};

export default Header;